import React from "react";
import { View, StyleSheet } from "react-native";
import { useNavigation, type NavigationProp } from "@react-navigation/native";
import { ThumbsUp, ThumbsDown } from "lucide-react-native";
import {
  QuestionLayout,
  SquareOptionButton,
} from "@src/packages/medical-history/components";
import { useTranslations } from "@src/packages/i18n";
import { useMedicationsVaccines } from "../MedicationsVaccinesContext";
import type { MedicationsVaccinesStackParamList } from "../MedicationsVaccinesNavigator";

export function MedicationsIntroScreen() {
  const t = useTranslations();
  const { state, updateState } = useMedicationsVaccines();
  const navigation =
    useNavigation<NavigationProp<MedicationsVaccinesStackParamList>>();

  const selection =
    state.currentlyTakingMedications == null
      ? null
      : state.currentlyTakingMedications
        ? "yes"
        : "no";

  const handleChange = (value: "yes" | "no") => {
    if (value === "yes") {
      updateState({
        currentlyTakingMedications: true,
        selectedProducts: state.selectedProducts.filter((p) => p !== "none"),
      });
    } else {
      // When "no" is selected, track all existing medication IDs for deletion
      const allExistingMedicationIds = state.medicationDetails
        .filter((detail) => !!detail.id)
        .map((detail) => detail.id!)
        .filter((id) => !(state.medicationIdsToDelete || []).includes(id)); // Avoid duplicates

      updateState({
        currentlyTakingMedications: false,
        selectedProducts: ["none"],
        medicationDetails: [], // Clear all (don't keep existing ones)
        medicationIdsToDelete: [
          ...(state.medicationIdsToDelete || []),
          ...allExistingMedicationIds,
        ],
      });
    }
  };

  const handleSave = () => {
    if (selection === "yes") {
      navigation.navigate("MedicationsProducts");
    } else if (selection === "no") {
      // If no medications, go to vaccines
      navigation.navigate("VaccinesSelect");
    }
  };

  return (
    <QuestionLayout
      title={t("med_history.medical.medications_vaccines.intro_title")}
      onBack={() => navigation.goBack()}
      onSave={handleSave}
      saveDisabled={!selection}
      saveLabel={t("med_history.medical.shared.continue")}
    >
      <View style={styles.optionsContainer}>
        <SquareOptionButton
          icon={ThumbsUp}
          label={t("med_history.shared.yes")}
          isSelected={selection === "yes"}
          onPress={() => handleChange("yes")}
          iconSize={24}
          containerSize={48}
          labelSize="lg"
        />
        <SquareOptionButton
          icon={ThumbsDown}
          label={t("med_history.shared.no")}
          isSelected={selection === "no"}
          onPress={() => handleChange("no")}
          iconSize={24}
          containerSize={48}
          labelSize="lg"
        />
      </View>
    </QuestionLayout>
  );
}

const styles = StyleSheet.create({
  optionsContainer: {
    flexDirection: "row",
    gap: 12,
    marginBottom: 40,
    width: "100%",
  },
});

export default MedicationsIntroScreen;
